import { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Anexo, Endpoint, getFilterDates, IeAnexo, Route, ToastServiceStipulator } from '@philips-emr/hpms-stipulator-commons';
import FilterService from '@philips-emr/hpms-stipulator-commons/lib/services/filter-service';
import HpmsCustomDialogAPI from '@philips-emr/hpms-stipulator-components/lib/api/hpms-custom-dialog-api';
import { HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { ActionAPI, BaseFileUploaderAPI, BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues, FetchAPI, FilterAPI, PanelAPI, RepeaterAPI, TextAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnChange, OnClear, OnClick, OnClose, OnDynamicContentRender, OnMount, OnUnmount } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, ClearEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { FileService, HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { HttpMethod } from '@philips-emr/tws-core/lib/services/http-service';
import { applicationContext } from '@philips-emr/tws-foundation';
import { isEmptyArrayOrObject, isEmptyOrBlankString } from '@philips-emr/tws-utils';

import { MovimentacaoLoteAliases as Aliases } from '../../codegen';
import BaseController from '../BaseController';

import { getLoteStampBySituacao, LoteData, LoteSituacao, onFailedAction, OptionKey } from './lote-utils';

@Controller({ fragment: 'movimentacao-lote' })
class MovimentacaoLoteController extends BaseController {

  private loteImportandoLote: boolean = false;

  private anexoModelo: Anexo | null = null;

  @Inject('fetch-lote')
  private fetchLotesApi: FetchAPI;
  @Inject('panel-errors')
  private panelErrorsApi: PanelAPI;
  @Inject('panel-loader')
  private panelLoaderApi: PanelAPI;
  @Inject('repeater-errors')
  private repeaterErrorsApi: RepeaterAPI;
  @Inject('panel-file-uploader-modal')
  private panelFileUploaderModalApi: PanelAPI;
  @Inject('hpms-custom-dialog')
  private customDialog: HpmsCustomDialogAPI;
  @Inject('action-modelo')
  private actionModeloApi: ActionAPI;

  @OnMount('panel-movimentacao-lote')
  onMountContent(): void {
    applicationContext.openModule('movimentacao-lote');
  }

  @OnMount({ target: 'contextual-bar' })
  onMountCtx(): void {
    void HttpService.get<Anexo>(Endpoint.GET.ANEXO_MODELO_LOTE)
      .then(response => {
        const { body = null } = response;
        this.anexoModelo = body;

        this.actionModeloApi.setEnabled(Boolean(body));
      }).catch(() => this.actionModeloApi.setEnabled(false));
  }

  @OnClick({ target: 'action-modelo' })
  onClickDownloadModelo(): void {
    if (this.anexoModelo) {
      const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
      const ieTabela = IeAnexo.MODELO_LOTE;
      const nrSeqAnexo = this.anexoModelo.nrSeqAnexo;

      FileService.download(Endpoint.GET.ANEXO_MODELO_LOTE_DOWNLOAD, this.anexoModelo.filename, { ieTabela, nrSeqAnexo, nrSeqContrato }, HttpMethod.GET)
        .catch(() => ToastServiceStipulator.error(MessageService.translate('stipulator.arquivo-nao-localizado')));
    }
  }

  @OnMount({ target: 'text-texto-padrao' })
  onCtxMount(event: MountEvent): void {
    const api = event.target as unknown as TextAPI;

    void HttpService.get<{ dsTexto: string }>(Endpoint.GET.TEXTO_PADRAO, { ieFuncionalidade: 3 })
      .then(response => {
        const { body = { dsTexto: '' } } = response;
        api.setValue(body.dsTexto);
      });
  }

  @OnClear({ target: 'lote-filter' })
  onClearFilter(event: ClearEvent): void {
    const api = event.target as unknown as FilterAPI;

    api.setData({});
    api.setData(getFilterDates('dtInicio', 'dtFim', 3));
  }

  @OnMount(Aliases.LOTE_FILTER)
  onFilterMount(event: MountEvent): void {
    FilterService.restoreFilterState(event);
  }

  @OnUnmount(Aliases.LOTE_FILTER)
  onUnmountFilter(event: MountEvent): void {
    FilterService.persistFilterState(event);
  }

  @OnDynamicContentRender({ target: 'hpms-custom-card-lote' })
  onDynamicContentRenderCardLote(event: RenderEvent): void {
    const { data, render } = event;
    const { ieSituacao, dsSituacao } = data as LoteData;

    const content = (): ReactElement => (getLoteStampBySituacao(ieSituacao, dsSituacao));

    render(content);
  }

  @HPMSOnOptionClick('hpms-custom-card-lote')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqLote } = data as unknown as LoteData;

    const action = {
      [OptionKey.DETAILS]: () => this.details(nrSeqLote),
      [OptionKey.RELEASE]: () => this.release(nrSeqLote),
      [OptionKey.EXCLUDE]: () => this.exclude(nrSeqLote),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @HPMSOnCreateOption('hpms-custom-card-lote')
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { ieSituacao, dtLiberacaoLoteInclusao } = data as unknown as LoteData;

    const show = {
      [OptionKey.DETAILS]: true,
      [OptionKey.RELEASE]: [LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO, LoteSituacao.PENDENTE].includes(ieSituacao),
      [OptionKey.EXCLUDE]: [LoteSituacao.AGUARDANDO_ACAO_DO_USUARIO, LoteSituacao.COM_INCONSISTENCIAS, LoteSituacao.PENDENTE].includes(ieSituacao) && !dtLiberacaoLoteInclusao,
    };

    shouldRender(show[key]);
  }

  @OnClick({ target: 'button-ok' })
  onClickOk(): void {
    this.customDialog.setVisible(false);
  }

  @OnClick({ target: 'action-import' })
  onClickImport(): void {
    this.panelFileUploaderModalApi.setVisible(true);
  }

  @OnChange({ target: 'file-uploader-modal' })
  onChangeFileUploaderModal(event: ChangeEvent<{ filename: string, filepath: string, size: number }[]>): void {
    const api = event.target as unknown as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;
    api.setValue(event.value);
  }

  @OnClose({ target: 'file-uploader-modal' })
  onCloseFileUploaderModal(event: CloseEvent): void {
    const api = event.target as unknown as BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;
    this.panelFileUploaderModalApi.setVisible(false);

    const files = api.getValue();
    if (!files || isEmptyArrayOrObject(files) || this.loteImportandoLote) {
      return;
    }

    this.loteImportandoLote = true;
    this.customDialog.setVisible(true);

    const [file] = files;
    const { filepath: path } = file;

    const { nrSeqContrato } = this.context.getBulkData();
    HttpService.post(Endpoint.POST.IMPORT_LOTE, { uri: path, nrSeqContrato })
      .then(() => {
        this.loteImportandoLote = false;
        this.customDialog.setVisible(false);
        this.forceFetch();
      }).catch(resp => {
        const { body } = resp;

        this.loteImportandoLote = false;
        const defaultMsg = (body ?? {})['956418'];

        const actionError = (data: any[]): void => {
          this.panelErrorsApi.setVisible(true);
          this.panelLoaderApi.setVisible(false);
          this.repeaterErrorsApi.setData(data);
        };

        if (Array.isArray(body)) {
          actionError(body);
        } else if (!isEmptyOrBlankString(defaultMsg)) {
          actionError([{ message: MessageService.translate(defaultMsg) }]);
        }

        this.forceFetch();
      });
  }

  private forceFetch = (): void => {
    this.fetchLotesApi.forceFetch();
  };

  private release = (nrSeqLote: number): void => {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    const params = { nrSeqLote, nrSeqContrato };

    HttpService.post(Endpoint.POST.RELEASE_LOTE, params)
      .then(() => this.forceFetch())
      .catch(response => onFailedAction(response, this.forceFetch));
  };

  private exclude = (nrSeqLote: number): void => {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    const params = { nrSeqLote, nrSeqContrato };

    HttpService.delete(Endpoint.DELETE.LOTE, params)
      .then(() => this.forceFetch())
      .catch(response => onFailedAction(response, this.forceFetch));
  };

  private details = (nrSeqLote: number): void => {
    this.context.set({ nrSeqLote });
    this.historyService.push(Route.LOTE, { nrSeqLote });
  };

}

export default MovimentacaoLoteController;
